/**
 * Compline bell — a slim full-width row that docks INSIDE the sticky header
 * (below the resource bars), exactly like the Infirmary/Dungeon status line, so
 * it never overlaps the bars and never fights the header's stacking context.
 *
 * The poll (~60s) keeps a module cache of the active blessing; both the poll and
 * every header rebuild (Shell.buildHeader calls `buildComplineRow`) render the
 * row from that cache, so it survives navigation. A soft bell chime sounds once
 * per toll (synthesised via WebAudio — no asset, no autoplay/copyright snags;
 * silently skipped if the browser blocks audio before the first user gesture).
 *
 * No FOMO: it can be dismissed for the current window, and missing it costs
 * nothing. Mounted once from main.ts; self-manages on a timer.
 */
import { api } from '../api/client.js';
import { store } from '../state/store.js';

const POLL_MS = 60_000;
let pollHandle: number | null = null;
let tickHandle: number | null = null;

// Module cache of the current bell state, so the header can render synchronously.
let active: { label: string; blurb: string; bonusPct: number } | null = null;
let endsAtMs = 0;
let dismissedKey: string | null = null; // endsAt of the window the user dismissed
let chimedKey: string | null = null; // endsAt of the window we already chimed for

function fmtCountdown(ms: number): string {
  if (ms <= 0) return 'fading';
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return m > 0 ? `${m}m ${String(s).padStart(2, '0')}s` : `${s}s`;
}

/** A gentle two-tone bell, synthesised so there's no audio file to ship and no
 *  autoplay/copyright concern. No-ops (never throws) if the browser blocks audio
 *  until the first user interaction. */
function playChime(): void {
  try {
    type WinAudio = typeof window & { webkitAudioContext?: typeof AudioContext };
    const Ctx = window.AudioContext ?? (window as WinAudio).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    if (ctx.state === 'suspended') void ctx.resume();
    const now = ctx.currentTime;
    for (const [freq, delay, peak] of [[660, 0, 0.16], [990, 0.06, 0.10]] as const) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      const t0 = now + delay;
      gain.gain.setValueAtTime(0.0001, t0);
      gain.gain.exponentialRampToValueAtTime(peak, t0 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 1.6);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t0);
      osc.stop(t0 + 1.7);
    }
    window.setTimeout(() => void ctx.close().catch(() => {}), 2200);
  } catch {
    /* audio blocked or unavailable — silent is fine */
  }
}

function ensureStyles(): void {
  if (document.getElementById('compline-styles')) return;
  const style = document.createElement('style');
  style.id = 'compline-styles';
  style.textContent = `
    #compline-row {
      flex: 0 0 100%;          /* wrap to a full-width row below the bars */
      box-sizing: border-box;
      margin-top: 6px;
      padding-top: 7px;
      border-top: 1px solid var(--c-rule, #b09a6a);
      display: flex;
      align-items: center;
      gap: 12px;
      color: var(--c-ink, #1c1108);
    }
    #compline-row .cb-art {
      width: 40px; height: 40px; flex: 0 0 40px;
      border-radius: 6px; object-fit: cover;
      border: 1px solid var(--c-rule, #b09a6a);
    }
    #compline-row .cb-body { flex: 1 1 auto; min-width: 0; }
    #compline-row .cb-title {
      font-family: var(--font-elegant, Georgia, serif);
      font-weight: 700; font-size: 0.96rem; color: var(--c-oxblood, #6b1f1f);
      display: flex; align-items: baseline; gap: 8px; flex-wrap: wrap;
    }
    #compline-row .cb-count { font-family: var(--font-numeric, monospace); font-size: 0.82rem; color: var(--c-ink-soft, #6b5a40); }
    #compline-row .cb-blurb { font-size: 0.84rem; line-height: 1.3; margin-top: 1px; }
    #compline-row .cb-close {
      flex: 0 0 auto; background: none; border: none; cursor: pointer;
      font-size: 1.15rem; line-height: 1; color: var(--c-ink-soft, #6b5a40);
      padding: 0 2px; align-self: center;
    }
    #compline-row .cb-close:hover { color: var(--c-oxblood, #6b1f1f); }
    @media (max-width: 480px) {
      #compline-row { gap: 8px; }
      #compline-row .cb-art { width: 34px; height: 34px; flex-basis: 34px; }
      #compline-row .cb-title { font-size: 0.88rem; }
      #compline-row .cb-blurb { font-size: 0.78rem; }
    }
  `;
  document.head.append(style);
}

function escapeText(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** Build the bell row from the module cache, or null if nothing to show. Pure
 *  render — NO chime here (it's called on every header rebuild). Exported so
 *  Shell.buildHeader can append it synchronously. */
export function buildComplineRow(): HTMLElement | null {
  if (!store.get().accessToken) return null;
  if (!active || !endsAtMs) return null;
  if (dismissedKey === String(endsAtMs)) return null;
  ensureStyles();

  const row = document.createElement('div');
  row.id = 'compline-row';
  row.setAttribute('role', 'status');
  row.innerHTML = `
    <img class="cb-art" src="/img/banners/compline-bell.webp" alt="" onerror="this.remove()" />
    <div class="cb-body">
      <div class="cb-title">
        <span>The bell of Compline tolls — ${escapeText(active.label)}</span>
        <span class="cb-count" id="cb-count">${fmtCountdown(endsAtMs - Date.now())}</span>
      </div>
      <div class="cb-blurb">${escapeText(active.blurb)} <strong>+${active.bonusPct}%</strong></div>
    </div>
    <button class="cb-close" id="cb-close" aria-label="Dismiss">×</button>
  `;
  row.querySelector<HTMLButtonElement>('#cb-close')?.addEventListener('click', () => {
    dismissedKey = String(endsAtMs);
    document.getElementById('compline-row')?.remove();
  });
  return row;
}

/** Reflect the current cache into the live header: remove any stale row, append
 *  a fresh one if the bell is ringing. Chimes once per new window. */
function syncRow(opts: { chime: boolean }): void {
  document.getElementById('compline-row')?.remove();
  const header = document.querySelector<HTMLElement>('.shell-header');
  if (!header) return;
  const row = buildComplineRow();
  if (!row) return;
  header.append(row);
  if (opts.chime && chimedKey !== String(endsAtMs)) {
    chimedKey = String(endsAtMs);
    playChime();
  }
}

async function refresh(): Promise<void> {
  if (!store.get().accessToken) {
    active = null; endsAtMs = 0;
    document.getElementById('compline-row')?.remove();
    return;
  }
  try {
    const s = await api.complineStatus();
    if (s.active && s.endsAt) {
      active = s.active;
      endsAtMs = Date.parse(s.endsAt);
      syncRow({ chime: true });
    } else {
      active = null; endsAtMs = 0;
      document.getElementById('compline-row')?.remove();
    }
  } catch {
    // transient — leave whatever is showing
  }
}

/** Mount once. Polls the bell status and self-manages the header row. */
export function mountComplineBanner(): void {
  void refresh();
  if (pollHandle === null) {
    pollHandle = window.setInterval(() => void refresh(), POLL_MS);
  }
  // One lightweight ticker updates whatever #cb-count currently exists (the row
  // may be rebuilt by navigation), and re-attaches the row if a header rebuild
  // dropped it while the bell is still ringing.
  if (tickHandle === null) {
    tickHandle = window.setInterval(() => {
      if (!active || !endsAtMs || dismissedKey === String(endsAtMs)) return;
      const countEl = document.getElementById('cb-count');
      if (countEl) {
        countEl.textContent = fmtCountdown(endsAtMs - Date.now());
      } else if (document.querySelector('.shell-header')) {
        // Header was rebuilt without the row (e.g. navigation) — re-attach it,
        // no chime (we only chime on a fresh toll, not on every page change).
        syncRow({ chime: false });
      }
    }, 1000);
  }
}
